import { type ElementType, type ReactElement, type ReactNode } from 'react';
import { resolveSemanticColor, type SemanticColorKey } from '@/theme/semanticTokens';
import {
  s,
  type SpacingToken,
  type TypographyRole,
  type TypographyToken,
  typographyTokens,
} from '@/theme/tokens';
import { useTheme } from './hooks';
import { Typography } from './Typography';
import type { SxProps, Theme } from './types';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const VARIANT_MAP: Partial<
  Record<TypographyRole, 'pageTitle' | 'sectionTitle' | 'body' | 'caption' | 'label' | 'labelTitle'>
> = {
  pageTitle: 'pageTitle',
  sectionTitle: 'sectionTitle',
  body: 'body',
  bodyStrong: 'body',
  caption: 'caption',
  captionSmall: 'caption',
  overline: 'label',
  chip: 'label',
  labelTitle: 'labelTitle',
};

const resolveSpacing = (token: SpacingToken | undefined): string | number | undefined => {
  if (!token) return undefined;
  return SPACING_MAP[token];
};

type TextProps = {
  role?: TypographyRole;
  color?: SemanticColorKey | 'inherit';
  component?: ElementType;
  gutterBottom?: boolean;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  truncate?: boolean;
  transform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  mb?: SpacingToken;
  htmlFor?: string;
  dataGridTypography?: 'default' | 'custom';
  className?: string;
  sx?: SxProps<Theme>;
  children: ReactNode;
};

export const Text = ({
  role = 'body',
  color,
  component,
  gutterBottom,
  align,
  truncate,
  transform,
  mb,
  htmlFor,
  dataGridTypography = 'default',
  className,
  sx,
  children,
}: TextProps): ReactElement => {
  const roleVariant = VARIANT_MAP[role];
  const useRoleVariant = Boolean(roleVariant);
  const theme = useTheme();
  const token = typographyTokens[role] as TypographyToken;
  const defaultColor = role === 'bodyMuted' ? 'textSecondary' : 'textPrimary';
  const colorKey = color ?? defaultColor;
  const resolvedComponent = component ?? (useRoleVariant ? undefined : token.component);
  const resolvedColor = colorKey === 'inherit' ? 'inherit' : resolveSemanticColor(theme, colorKey);
  const textTransform = transform ?? (useRoleVariant ? undefined : token.textTransform);
  const fontWeightOverride = role === 'bodyStrong' ? 600 : undefined;
  const baseSx: SxProps<Theme> = {
    color: resolvedColor,
    ...(useRoleVariant
      ? {
          ...(fontWeightOverride ? { fontWeight: fontWeightOverride } : {}),
          ...(token.fontSize ? { fontSize: token.fontSize } : {}),
        }
      : {
          fontWeight: token.fontWeight,
          ...(token.letterSpacing ? { letterSpacing: token.letterSpacing } : {}),
          ...(token.fontSize ? { fontSize: token.fontSize } : {}),
        }),
    ...(truncate ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
    ...(textTransform ? { textTransform } : {}),
    ...(mb ? { mb: resolveSpacing(mb) } : {}),
  };
  const resolvedSx = sx ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])] : baseSx;
  return (
    <Typography
      variant={roleVariant ?? token.variant}
      {...(resolvedComponent ? { component: resolvedComponent } : {})}
      {...(htmlFor ? { htmlFor } : {})}
      {...(dataGridTypography === 'custom' ? { 'data-grid-typography': 'custom' } : {})}
      className={className}
      gutterBottom={gutterBottom}
      align={align}
      sx={resolvedSx}
    >
      {children}
    </Typography>
  );
};
